import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BillingDetails } from "@/types/billing";
import { useUserContextStore } from "@/utils/store";
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  Database,
  MessageSquare,
  Settings,
  Shield,
  Star,
  Users,
  Zap
} from "lucide-react";
import React, { useState } from "react";
import BillingDetailsForm from "./BillingDetailsForm";

const BillingPage: React.FC = () => {
  const { userContext } = useUserContextStore();
  const currentTier = userContext?.tier || 'FREE';
  const [showBillingForm, setShowBillingForm] = useState(false);
  const [billingDetails, setBillingDetails] = useState<BillingDetails | null>(null);
  
  const features = [
    {
      icon: <Database className="w-5 h-5" />,
      title: "1 GB Storage",
      description: "Per database instance",
      included: true,
    },
    {
      icon: <Users className="w-5 h-5" />,
      title: "3 User Types",
      description: "Super Admin, Read/Write, Read Only",
      included: true,
    },
    {
      icon: <Shield className="w-5 h-5" />,
      title: "Security",
      description: "Encrypted connections & backups",
      included: true,
    },
    {
      icon: <Zap className="w-5 h-5" />,
      title: "High Performance",
      description: "Optimized database engines",
      included: true,
    },
    {
      icon: <Clock className="w-5 h-5" />,
      title: "24/7 Monitoring",
      description: "Automatic capacity monitoring",
      included: true,
    },
  ];

  const upcomingFeatures = [
    "Unlimited storage plans",
    "Advanced analytics dashboard",
    "Custom backup schedules",
    "Team collaboration tools",
    "API rate limiting controls",
    "Priority support",
  ];

  const handleBillingSuccess = (details: BillingDetails) => {
    setBillingDetails(details);
    setShowBillingForm(false);
  };

  const handleCheckout = (checkoutUrl: string) => {
    // This will be handled by the form component
    console.log('Redirecting to:', checkoutUrl);
  };

  if (showBillingForm) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Billing Details</h1>
              <Button
                variant="outline"
                onClick={() => setShowBillingForm(false)}
              >
                Back to Billing
              </Button>
            </div>
            <BillingDetailsForm
              onSuccess={handleBillingSuccess}
              onCheckout={handleCheckout}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
      
        <div className="grid lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {/* Current Plan Card */}
          <div className="lg:col-span-2">
            <Card className="border-2 border-gray-200 bg-white">
              <CardHeader className="text-center pb-6">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Badge variant="secondary" className="bg-gray-100 text-gray-800 border-gray-200">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Current Plan
                  </Badge>
                  <Badge variant="outline" className="bg-gray-100 text-gray-800 border-gray-200">
                    <Star className="w-4 h-4 mr-1" />
                    Beta
                  </Badge>
                </div>
                <CardTitle className="text-3xl font-bold text-gray-900 mb-2">
                  {currentTier === 'FREE' ? 'Free Tier' : 'Paid Tier'}
                </CardTitle>
                <div className="text-6xl font-bold text-gray-900 mb-2">
                  {currentTier === 'FREE' ? '$0' : '$TBD'}
                </div>
                <p className="text-gray-600">
                  {currentTier === 'FREE' ? 'Forever free during beta' : 'Coming soon'}
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="text-gray-700 mt-0.5">
                        {feature.icon}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{feature.title}</h4>
                        <p className="text-sm text-gray-600">{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-gray-100 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">
                        Need More Storage?
                      </h4>
                      <p className="text-sm text-gray-700 mb-3">
                        If you need more than 1GB of storage or have specific requirements, 
                        we're here to help. Contact us for custom solutions.
                      </p>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-gray-300 text-gray-700 hover:bg-gray-100"
                        onClick={() => {
                          window.open('mailto:contact@quickdb.com?subject=Storage%20Inquiry', '_blank');
                        }}
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Contact Sales
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Coming Soon Card */}
          <div className="space-y-6">
            <Card className="border-2 border-gray-200 bg-white">
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="bg-gray-100 text-gray-800 border-gray-200">
                    <Clock className="w-4 h-4 mr-1" />
                    Coming Soon
                  </Badge>
                </div>
                <CardTitle className="text-2xl font-bold text-gray-900">
                  Pro Plans
                </CardTitle>
                <p className="text-gray-600">
                  Advanced features for growing teams
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {upcomingFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <div className="pt-4 border-t border-gray-200">
                  <Button 
                    variant="outline" 
                    className="w-full border-gray-300 text-gray-700 hover:bg-gray-100"
                    onClick={() => setShowBillingForm(true)}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Manage Billing Details
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* FAQ Section */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-900">Frequently Asked Questions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">When will paid plans be available?</h4>
                    <p className="text-sm text-gray-600">
                      We're working on flexible pricing plans. Sign up for our newsletter to be notified.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Can I upgrade my storage limit?</h4>
                    <p className="text-sm text-gray-600">
                      Yes! Contact us for custom storage solutions and enterprise plans.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">What happens after beta?</h4>
                    <p className="text-sm text-gray-600">
                      {currentTier === 'FREE' 
                        ? 'Free tier users will be grandfathered in with their current limits.'
                        : 'Paid tier users will have priority access to new features and support.'
                      }
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingPage; 